import React from "react";
import { ChevronRight, Upload } from "lucide-react";
import { AppModal, AppModalHeader } from "../../../components/AppModal";
import { APP_BTN_OUTLINE_CLASS, APP_BTN_PRIMARY_CLASS } from "../../../ui/controlStyles";
import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { OfferImportSourcePicker, type OfferImportSource } from "./OfferImportSourcePicker";

/** Zeile wie vom Server (server/services/lvPdf/parseLvText.ts, LvRow). */
type LvRow = {
  kind: "title" | "item" | "text";
  oz: string;
  title_level: number | null;
  short_text: string;
  long_text: string;
  quantity: number | null;
  unit: string;
  unit_price: number | null;
  is_eventual: boolean;
  source: "rules" | "ai";
  /** Position: Gesamtpreis wie im PDF gedruckt. */
  pdf_total?: number | null;
  /** Titel: im PDF gedruckte Titelsumme und Summe aus der Zusammenstellung (server: parseOfferLayout). */
  pdf_sum?: number | null;
  pdf_summary_sum?: number | null;
};

type JobState =
  | { status: "running"; phase: "pages" | "vision" | "sections"; done: number; total: number }
  | {
      status: "done";
      rows: LvRow[];
      filename: string;
      pageCount: number;
      skippedPages: number;
      visionPages: number;
      visionSkipped: number;
      aiSections: number;
      unverifiedSections: number;
      statedNetTotal?: number | null;
      computedNetTotal?: number | null;
      terms?: LvTerms;
    }
  | { status: "error"; error: string };

/** Konditionen aus dem PDF (server/services/lvPdf/lvTerms.ts). */
type LvTermHit = { percent: number; page: number; text: string };
type LvTerms = {
  discount?: LvTermHit;
  skonto?: LvTermHit & { days: number | null };
  siteLevy?: LvTermHit;
  retention?: LvTermHit;
};

const POLL_MS = 2000;
/**
 * Weichen zwei Summen ab? Unter 1 € bzw. 0,05 % ist es Rundung: Branchensoftware rechnet Titelsummen
 * oft aus ungerundeten Einzelpreisen.
 */
function sumsDiffer(a: number | null | undefined, b: number): boolean {
  return a != null && Math.abs(a - b) > Math.max(1, Math.abs(b) * 0.0005);
}

function phaseLabel(s: Extract<JobState, { status: "running" }>): string {
  if (s.phase === "vision") return `Gescannte Seiten werden gelesen… (${s.total} Seiten)`;
  if (s.phase === "sections" && s.total > 0) return `Positionen werden geprüft… (${s.done}/${s.total} Abschnitte)`;
  return "PDF wird gelesen…";
}

function formatPercent(p: number): string {
  return `${p.toLocaleString("de-DE", { maximumFractionDigits: 2 })} %`;
}

function formatEuro(v: number): string {
  return v.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

/** Gesamtpreis einer Position: wie im PDF gedruckt, sonst Menge × EP; null ohne Preis. */
function lineTotal(r: LvRow): number | null {
  if (r.kind !== "item" || r.unit_price == null) return null;
  return r.pdf_total ?? round2((r.quantity ?? 1) * r.unit_price);
}

function formatQty(q: number | null): string {
  if (q == null) return "";
  return q.toLocaleString("de-DE", { maximumFractionDigits: 3 });
}

async function readJson<T>(res: Response): Promise<T & { error?: string }> {
  const raw = await res.text();
  try {
    return (raw ? JSON.parse(raw) : {}) as T & { error?: string };
  } catch {
    return { error: raw.trim() } as T & { error?: string };
  }
}

const CHECKBOX_CLASS = "h-3.5 w-3.5 cursor-pointer accent-pipedrive-blue align-middle";

const EventualTag: React.FC = () => (
  <span className="ml-2 rounded border border-app-border px-1 py-px text-[10px] font-medium text-app-text-muted" title="Bedarfsposition, nicht in der Summe">
    Bedarf
  </span>
);

function plural(n: number, one: string, many: string): string {
  return `${n.toLocaleString("de-DE")} ${n === 1 ? one : many}`;
}

/** j liegt im Teilbaum von i (i ist Vorfahr von j). */
function isAncestor(parent: number[], i: number, j: number): boolean {
  for (let p = parent[j]; p >= 0; p = parent[p]) if (p === i) return true;
  return false;
}

function signedEuro(v: number): string {
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}${formatEuro(Math.abs(v))}`;
}

/**
 * Erklärt, wo die Differenz zur Nettosumme im PDF entsteht. Stimmen Positionen und Titelsumme im PDF
 * überein, nur die Zusammenstellung nennt etwas anderes, widerspricht sich das PDF selbst.
 */
const SumMismatchNotice: React.FC<{
  rows: LvRow[];
  stated: number;
  computed: number;
  deepest: number[];
  mismatch: Map<number, { positions: number; pdfSum: number | null; summarySum: number | null }>;
  onReveal: (i: number) => void;
}> = ({ rows, stated, computed, deepest, mismatch, onReveal }) => {
  const total = round2(stated - computed);
  const shown = deepest.slice(0, 4);
  let explained = 0;
  const items = shown.map((i) => {
    const m = mismatch.get(i)!;
    const r = rows[i];
    const name = [r.oz, r.short_text].filter(Boolean).join(" ");
    const internal =
      m.pdfSum != null && !sumsDiffer(m.pdfSum, m.positions) && m.summarySum != null;
    const pdfValue = internal ? m.summarySum! : (m.summarySum ?? m.pdfSum ?? m.positions);
    const delta = round2(pdfValue - m.positions);
    explained += delta;
    return (
      <li key={i}>
        <button type="button" onClick={() => onReveal(i)} className="font-semibold underline decoration-amber-400 underline-offset-2 hover:decoration-amber-700">
          {name}
        </button>
        {internal ? (
          <>
            : Die Positionen ergeben {formatEuro(m.positions)}, genau wie die Titelsumme im PDF. Die Zusammenstellung am Ende des
            PDFs nennt aber {formatEuro(pdfValue)} ({signedEuro(delta)}). Das PDF widerspricht sich hier selbst.
          </>
        ) : (
          <>
            : Die Positionen ergeben {formatEuro(m.positions)}, das PDF nennt {formatEuro(pdfValue)} ({signedEuro(delta)}). Bitte
            die Positionen dieses Titels mit dem PDF vergleichen.
          </>
        )}
      </li>
    );
  });
  const rest = round2(total - explained);
  return (
    <div className="space-y-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <p>
        Die Positionen ergeben {formatEuro(computed)}, im PDF steht als Nettosumme {formatEuro(stated)} (
        {signedEuro(total)}).
        {shown.length > 0
          ? " Die Differenz entsteht hier:"
          : rows.some((r) => r.kind === "title" && (r.pdf_sum != null || r.pdf_summary_sum != null))
            ? " Die Titelsummen im PDF passen zu den Positionen; die Abweichung steckt in der Gesamtsumme des PDFs."
            : " Das PDF druckt keine Titelsummen, an denen sich die Stelle eingrenzen lässt. Bitte die Titelsummen mit dem PDF vergleichen."}
      </p>
      {shown.length > 0 ? <ul className="list-disc space-y-1 pl-4">{items}</ul> : null}
      {shown.length > 0 && Math.abs(rest) >= 0.01 ? (
        <p className="text-amber-800">
          {Math.abs(rest) <= Math.max(5, stated * 0.0005)
            ? `Die übrigen ${signedEuro(rest)} sind Rundungsdifferenzen der Einzelpreise.`
            : `Weitere ${signedEuro(rest)} lassen sich keinem Titel zuordnen.`}
        </p>
      ) : null}
    </div>
  );
};

/** Für jede Zeile der nächste übergeordnete Titel (Index) und die Einrückung. */
function buildTree(rows: LvRow[]): { parent: number[]; indent: number[] } {
  const parent: number[] = [];
  const indent: number[] = [];
  const stack: Array<{ index: number; level: number }> = [];
  rows.forEach((r, i) => {
    if (r.kind === "title") {
      const level = Math.max(1, r.title_level ?? 1);
      while (stack.length > 0 && stack[stack.length - 1].level >= level) stack.pop();
      parent.push(stack.length > 0 ? stack[stack.length - 1].index : -1);
      indent.push(level - 1);
      stack.push({ index: i, level });
    } else {
      parent.push(stack.length > 0 ? stack[stack.length - 1].index : -1);
      indent.push(stack.length);
    }
  });
  return { parent, indent };
}

/** LV aus PDF: Datei/Quelle wählen → Seiten lesen und Zeilen erkennen → Vorschau mit Auswahl → ins Angebot übernehmen. */
export const LvPdfImportDialog: React.FC<{
  offeringId: string;
  open: boolean;
  onClose: () => void;
  onImported?: () => void | Promise<void>;
  /** Liegt eine GAEB-Datei im Projekt, kann der Nutzer direkt dorthin wechseln. */
  onSwitchToGaeb?: () => void;
}> = ({ offeringId, open, onClose, onImported, onSwitchToGaeb }) => {
  const [jobId, setJobId] = React.useState<string | null>(null);
  const [state, setState] = React.useState<JobState | null>(null);
  const [selected, setSelected] = React.useState<Set<number>>(new Set());
  const [collapsed, setCollapsed] = React.useState<Set<number>>(new Set());
  const [busySource, setBusySource] = React.useState<OfferImportSource | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [gaebHint, setGaebHint] = React.useState<string | null>(null);
  const [applyDiscount, setApplyDiscount] = React.useState(true);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const running = state?.status === "running" || busySource != null;

  const reset = () => {
    setJobId(null);
    setState(null);
    setSelected(new Set());
    setCollapsed(new Set());
    setBusySource(null);
    setError(null);
    setApplyDiscount(true);
  };

  const close = () => {
    if (saving) return;
    reset();
    onClose();
  };

  // GAEB im Projekt? Die Datei ist immer genauer als das PDF.
  React.useEffect(() => {
    if (!open || !onSwitchToGaeb) return;
    let cancelled = false;
    fetch(`/api/gaeb/offerings/${encodeURIComponent(offeringId)}/import-candidates?kind=gaeb`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((data: { items?: Array<{ filename: string }> }) => {
        if (!cancelled) setGaebHint(data.items?.[0]?.filename ?? null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, offeringId, onSwitchToGaeb]);

  // Stand des Auslese-Jobs abfragen.
  React.useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const res = await fetch(
          `/api/lv-pdf/offerings/${encodeURIComponent(offeringId)}/extract/${encodeURIComponent(jobId)}`,
        );
        const data = await readJson<JobState>(res);
        if (cancelled) return;
        if (!res.ok) throw new Error(data.error || "Auslesen fehlgeschlagen.");
        setState(data);
        if (data.status === "running") {
          timer = setTimeout(poll, POLL_MS);
        } else {
          setBusySource(null);
          if (data.status === "done") {
            // Alles vorausgewählt außer Hinweistexten (Vorbemerkungen sind oft lang).
            setSelected(new Set(data.rows.map((r, i) => (r.kind === "text" ? -1 : i)).filter((i) => i >= 0)));
            // Erst die Gliederung zeigen: alle Titel zugeklappt.
            setCollapsed(new Set(data.rows.map((r, i) => (r.kind === "title" ? i : -1)).filter((i) => i >= 0)));
          }
        }
      } catch (e) {
        if (cancelled) return;
        setBusySource(null);
        setState({ status: "error", error: e instanceof Error ? e.message : "Auslesen fehlgeschlagen." });
      }
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [jobId, offeringId]);

  const startExtraction = async (input: File | OfferImportSource) => {
    setError(null);
    setState({ status: "running", phase: "pages", done: 0, total: 0 });
    if (!(input instanceof File)) setBusySource(input);
    try {
      let res: Response;
      if (input instanceof File) {
        const fd = new FormData();
        fd.append("file", input);
        res = await fetch(`/api/lv-pdf/offerings/${encodeURIComponent(offeringId)}/extract`, { method: "POST", body: fd });
      } else {
        res = await fetch(`/api/lv-pdf/offerings/${encodeURIComponent(offeringId)}/extract`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ source: input }),
        });
      }
      const data = await readJson<{ jobId?: string }>(res);
      if (!res.ok || !data.jobId) throw new Error(data.error || `Auslesen fehlgeschlagen (${res.status}).`);
      setJobId(data.jobId);
    } catch (e) {
      setBusySource(null);
      setState({ status: "error", error: e instanceof Error ? e.message : "Auslesen fehlgeschlagen." });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const rows = React.useMemo(() => (state?.status === "done" ? state.rows : []), [state]);
  const { parent, indent } = React.useMemo(() => buildTree(rows), [rows]);
  const visible = React.useMemo(() => {
    const out = new Array<boolean>(rows.length).fill(true);
    for (let i = 0; i < rows.length; i += 1) {
      let p = parent[i];
      while (p >= 0) {
        if (collapsed.has(p)) {
          out[i] = false;
          break;
        }
        p = parent[p];
      }
    }
    return out;
  }, [rows, parent, collapsed]);

  const subtree = (titleIndex: number): number[] => {
    const level = Math.max(1, rows[titleIndex].title_level ?? 1);
    const out: number[] = [];
    for (let j = titleIndex + 1; j < rows.length; j += 1) {
      const r = rows[j];
      if (r.kind === "title" && Math.max(1, r.title_level ?? 1) <= level) break;
      out.push(j);
    }
    return out;
  };

  const toggle = (i: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const affected = rows[i].kind === "title" ? [i, ...subtree(i)] : [i];
      const turnOn = !next.has(i);
      for (const j of affected) {
        if (turnOn) next.add(j);
        else next.delete(j);
      }
      return next;
    });

  const toggleCollapsed = (i: number) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  /**
   * Titelsummen wie im Angebot: Positionen im Teilbaum, nur ausgewählte, ohne Bedarfspositionen.
   * Ein Durchlauf, jede Position zählt bei allen übergeordneten Titeln mit.
   */
  const totals = React.useMemo(() => {
    const titleSum = new Array<number>(rows.length).fill(0);
    const titleItems = new Array<number>(rows.length).fill(0);
    let net = 0;
    let eventualNet = 0;
    rows.forEach((r, i) => {
      if (r.kind !== "item") return;
      const gp = lineTotal(r);
      for (let p = parent[i]; p >= 0; p = parent[p]) titleItems[p] += 1;
      if (gp == null || !selected.has(i)) return;
      if (r.is_eventual) {
        eventualNet += gp;
        return;
      }
      net += gp;
      for (let p = parent[i]; p >= 0; p = parent[p]) titleSum[p] += gp;
    });
    return { titleSum, titleItems, net: round2(net), eventualNet: round2(eventualNet) };
  }, [rows, parent, selected]);

  /**
   * Abgleich je Titel mit den im PDF gedruckten Summen, unabhängig von der Auswahl: alle Positionen
   * des Teilbaums ohne Bedarfspositionen. Gemeldet werden die tiefsten abweichenden Titel, damit die
   * Erklärung auf die Stelle zeigt, an der die Differenz entsteht.
   */
  const sumCheck = React.useMemo(() => {
    const full = new Array<number>(rows.length).fill(0);
    rows.forEach((r, i) => {
      if (r.kind !== "item" || r.is_eventual) return;
      const gp = lineTotal(r);
      if (gp == null) return;
      for (let p = parent[i]; p >= 0; p = parent[p]) full[p] += gp;
    });
    const off = sumsDiffer;
    const mismatch = new Map<number, { positions: number; pdfSum: number | null; summarySum: number | null }>();
    rows.forEach((r, i) => {
      if (r.kind !== "title") return;
      const positions = round2(full[i]);
      if (off(r.pdf_sum, positions) || off(r.pdf_summary_sum, positions)) {
        mismatch.set(i, { positions, pdfSum: r.pdf_sum ?? null, summarySum: r.pdf_summary_sum ?? null });
      }
    });
    const deepest = [...mismatch.keys()].filter((i) => ![...mismatch.keys()].some((j) => j !== i && isAncestor(parent, i, j)));
    return { mismatch, deepest };
  }, [rows, parent]);

  /** Titel aufklappen (samt übergeordneten) und hinscrollen. */
  const revealRow = (i: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.delete(i);
      for (let p = parent[i]; p >= 0; p = parent[p]) next.delete(p);
      return next;
    });
    requestAnimationFrame(() => {
      document.querySelector(`[data-lv-row="${i}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
  };

  const titleIndexes = React.useMemo(
    () => rows.map((r, i) => (r.kind === "title" ? i : -1)).filter((i) => i >= 0),
    [rows],
  );

  const itemCount = rows.filter((r) => r.kind === "item").length;
  const selectedItems = rows.filter((r, i) => selected.has(i) && r.kind === "item").length;
  const eventualCount = rows.filter((r) => r.kind === "item" && r.is_eventual).length;
  const hasPrices = rows.some((r) => r.kind === "item" && r.unit_price != null);
  const terms: LvTerms = state?.status === "done" ? (state.terms ?? {}) : {};
  const totalCheck = (() => {
    if (state?.status !== "done") return null;
    const stated = state.statedNetTotal;
    const computed = state.computedNetTotal;
    if (stated == null || computed == null) return null;
    // Rundung je Position: 0,1 % Toleranz; mehr ist ein Lesefehler oder ein Fehler im PDF selbst.
    return { stated, computed, ok: Math.abs(stated - computed) <= Math.max(1, stated * 0.001) };
  })();

  const runImport = async () => {
    const chosen = rows
      .filter((_, i) => selected.has(i))
      .map(({ kind, oz, title_level, short_text, long_text, quantity, unit, unit_price, is_eventual }) => ({
        kind,
        oz,
        title_level,
        short_text,
        long_text,
        quantity,
        unit,
        unit_price,
        is_eventual,
      }));
    if (chosen.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/lv-pdf/offerings/${encodeURIComponent(offeringId)}/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rows: chosen,
          discountPercent: applyDiscount && state?.status === "done" ? (state.terms?.discount?.percent ?? null) : null,
        }),
      });
      const data = await readJson<{ inserted?: number }>(res);
      if (!res.ok) throw new Error(data.error || "LV konnte nicht übernommen werden.");
      await onImported?.();
      setSaving(false);
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : "LV konnte nicht übernommen werden.");
      setSaving(false);
    }
  };

  const discountPercent = applyDiscount ? (terms.discount?.percent ?? null) : null;
  const allExpanded = collapsed.size === 0;
  const notes =
    state?.status === "done"
      ? [
          state.skippedPages > 0 ? `${plural(state.skippedPages, "Seite", "Seiten")} ohne LV übersprungen` : null,
          state.visionPages > 0 ? `${plural(state.visionPages, "gescannte Seite", "gescannte Seiten")} per KI gelesen` : null,
          state.visionSkipped > 0 ? `${plural(state.visionSkipped, "gescannte Seite", "gescannte Seiten")} nicht gelesen (Limit)` : null,
          state.aiSections > 0 ? `${plural(state.aiSections, "Abschnitt", "Abschnitte")} per KI ergänzt (mit „KI“ markiert)` : null,
          state.unverifiedSections > 0
            ? `${plural(state.unverifiedSections, "Abschnitt", "Abschnitte")} mit Unstimmigkeiten, bitte Mengen prüfen`
            : null,
        ].filter((n): n is string => Boolean(n))
      : [];

  return (
    <AppModal
      open={open}
      onClose={close}
      disableClose={saving}
      overlayClassName="z-[260]"
      panelClassName={state?.status === "done" ? "md:max-w-6xl md:h-[90vh]" : "md:max-w-lg"}
      ariaLabelledBy="lv-pdf-dialog-title"
    >
      <AppModalHeader
        titleId="lv-pdf-dialog-title"
        title="Leistungsverzeichnis aus PDF"
        onClose={close}
        closeDisabled={saving}
      />

      <div
        className={
          state?.status === "done" && rows.length > 0
            ? "flex min-h-0 flex-1 flex-col"
            : "min-h-0 flex-1 overflow-y-auto px-5 py-4 space-y-4"
        }
      >
        {state?.status === "done" ? (
          rows.length === 0 ? (
            <p className="text-sm text-app-text-muted">
              Im PDF wurden keine LV-Positionen erkannt, auch nicht per KI. Ist es das richtige Dokument und gut lesbar? Eine
              GAEB-Datei ist immer zuverlässiger.
            </p>
          ) : (
            <>
              <div className="shrink-0 space-y-3 border-b border-app-border px-5 py-3">
                <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                  <p className="min-w-0 text-sm text-app-text-muted">
                    <span className="font-medium text-app-text">{state.filename}</span>
                    <span className="text-app-text-subtle">
                      {" · "}
                      {[
                        plural(state.pageCount, "Seite", "Seiten"),
                        plural(itemCount, "Position", "Positionen"),
                        titleIndexes.length > 0 ? plural(titleIndexes.length, "Titel", "Titel") : null,
                        eventualCount > 0 ? `davon ${plural(eventualCount, "Bedarfsposition", "Bedarfspositionen")}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </p>
                  {titleIndexes.length > 0 ? (
                    <button
                      type="button"
                      className="shrink-0 text-xs font-medium text-app-text-muted hover:text-app-text"
                      onClick={() => setCollapsed(allExpanded ? new Set(titleIndexes) : new Set())}
                    >
                      {allExpanded ? "Alle Titel einklappen" : "Alle Titel ausklappen"}
                    </button>
                  ) : null}
                </div>

                {notes.length > 0 ? <p className="text-xs text-app-text-subtle">{notes.join(" · ")}</p> : null}

                {totalCheck && !totalCheck.ok ? (
                  <SumMismatchNotice
                    rows={rows}
                    stated={totalCheck.stated}
                    computed={totalCheck.computed}
                    deepest={sumCheck.deepest}
                    mismatch={sumCheck.mismatch}
                    onReveal={revealRow}
                  />
                ) : null}

                {terms.discount || terms.skonto || terms.siteLevy || terms.retention ? (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-app-text-muted">
                    <span className="text-app-text-subtle">Im PDF gefunden:</span>
                    {terms.discount ? (
                      <label
                        className="inline-flex cursor-pointer items-center gap-1.5 text-app-text"
                        title={`„${terms.discount.text}“ (S. ${terms.discount.page})`}
                      >
                        <input
                          type="checkbox"
                          className={CHECKBOX_CLASS}
                          checked={applyDiscount}
                          onChange={(e) => setApplyDiscount(e.target.checked)}
                        />
                        {formatPercent(terms.discount.percent)} Rabatt ins Angebot übernehmen
                      </label>
                    ) : null}
                    {[
                      terms.skonto
                        ? `${formatPercent(terms.skonto.percent)} Skonto${terms.skonto.days != null ? ` in ${terms.skonto.days} Tagen` : ""}`
                        : null,
                      terms.siteLevy ? `${formatPercent(terms.siteLevy.percent)} Umlage` : null,
                      terms.retention ? `${formatPercent(terms.retention.percent)} Sicherheitseinbehalt` : null,
                    ]
                      .filter(Boolean)
                      .map((t) => (
                        <span key={t}>{t}</span>
                      ))}
                    {terms.skonto || terms.siteLevy || terms.retention ? (
                      <span className="text-app-text-subtle">(nur zur Info)</span>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="min-h-[16rem] flex-1 overflow-auto">
                <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
                  <colgroup>
                    <col className="w-10" />
                    <col className="w-24" />
                    <col />
                    <col className="w-20" />
                    <col className="w-16" />
                    {hasPrices ? <col className="w-28" /> : null}
                    {hasPrices ? <col className="w-32" /> : null}
                  </colgroup>
                  <thead className="sticky top-0 z-10 bg-app-surface text-xs text-app-text-subtle">
                    <tr className="[&>th]:border-b [&>th]:border-app-border [&>th]:py-2 [&>th]:font-medium">
                      <th className="pl-5 pr-1 text-left">
                        <input
                          type="checkbox"
                          className={CHECKBOX_CLASS}
                          aria-label="Alle auswählen"
                          checked={selected.size === rows.length}
                          ref={(el) => {
                            if (el) el.indeterminate = selected.size > 0 && selected.size < rows.length;
                          }}
                          onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((_, i) => i)) : new Set())}
                        />
                      </th>
                      <th className="px-2 text-left">OZ</th>
                      <th className="px-2 text-left">Bezeichnung</th>
                      <th className="px-2 text-right">Menge</th>
                      <th className="px-2 text-left">Einheit</th>
                      {hasPrices ? <th className="px-2 text-right">EP</th> : null}
                      {hasPrices ? <th className="pl-2 pr-5 text-right">GP</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      if (!visible[i]) return null;
                      const isOn = selected.has(i);
                      const depth = indent[i];
                      const pad = { paddingLeft: `${0.5 + depth * 1.25}rem` };
                      const cell = "border-b border-app-border/70 px-2 align-top";
                      const muted = isOn ? "" : "opacity-45";
                      const aiMark =
                        r.source === "ai" ? (
                          <span className="ml-2 text-[11px] font-medium text-sky-700" title="Abschnitt per KI ergänzt">
                            KI
                          </span>
                        ) : null;
                      const checkbox = (label: string) => (
                        <td className={`${cell} pl-5 pr-1 ${r.kind === "title" ? "pt-3 pb-1.5" : "py-1.5"}`}>
                          <input type="checkbox" className={CHECKBOX_CLASS} checked={isOn} onChange={() => toggle(i)} aria-label={label} />
                        </td>
                      );

                      if (r.kind === "title") {
                        const top = Math.max(1, r.title_level ?? 1) === 1;
                        const pt = "pt-3 pb-1.5";
                        const diff = sumCheck.mismatch.get(i);
                        const pdfShown = diff
                          ? sumsDiffer(diff.summarySum, diff.positions)
                            ? diff.summarySum
                            : diff.pdfSum
                          : null;
                        return (
                          <tr key={i} className={muted} data-lv-row={i}>
                            {checkbox(`${r.short_text} auswählen`)}
                            <td className={`${cell} ${pt} tabular-nums text-app-text-muted ${top ? "font-semibold" : ""}`}>{r.oz}</td>
                            <td className={`${cell} ${pt}`} colSpan={3} style={pad}>
                              <button
                                type="button"
                                onClick={() => toggleCollapsed(i)}
                                aria-expanded={!collapsed.has(i)}
                                className={`inline-flex items-start gap-1 text-left text-app-text hover:text-app-text ${top ? "font-semibold" : "font-medium"}`}
                              >
                                <ChevronRight
                                  className={`mt-0.5 h-4 w-4 shrink-0 text-app-text-subtle transition-transform ${collapsed.has(i) ? "" : "rotate-90"}`}
                                />
                                <span>{r.short_text}</span>
                                {aiMark}
                              </button>
                            </td>
                            {hasPrices ? <td className={`${cell} ${pt}`} /> : null}
                            {hasPrices ? (
                              <td
                                className={`${cell} ${pt} pr-5 text-right tabular-nums whitespace-nowrap ${top ? "font-semibold text-app-text" : "text-app-text-muted"}`}
                              >
                                {formatEuro(totals.titleSum[i])}
                                {pdfShown != null ? (
                                  <span
                                    className="block text-[11px] font-normal text-amber-700"
                                    title="Diese Summe steht so im PDF und passt nicht zu den Positionen darunter."
                                  >
                                    PDF: {formatEuro(pdfShown)}
                                  </span>
                                ) : null}
                              </td>
                            ) : null}
                          </tr>
                        );
                      }

                      if (r.kind === "text") {
                        const first = r.short_text || r.long_text.split("\n")[0];
                        const rest = r.long_text.split("\n").slice(r.long_text.startsWith(first) ? 1 : 0).join("\n").trim();
                        return (
                          <tr key={i} className={muted}>
                            {checkbox("Hinweis auswählen")}
                            <td className={`${cell} py-1.5`} />
                            <td className={`${cell} py-1.5`} colSpan={hasPrices ? 5 : 3} style={pad}>
                              <details className="text-xs text-app-text-muted">
                                <summary className="cursor-pointer list-none italic">
                                  {/^hinweis\b/i.test(first) ? first : `Hinweis: ${first}`}
                                </summary>
                                {rest ? <p className="mt-1 whitespace-pre-line text-app-text-subtle">{rest}</p> : null}
                              </details>
                            </td>
                          </tr>
                        );
                      }

                      const gp = lineTotal(r);
                      return (
                        <tr key={i} className={`${muted} hover:bg-app-hover/50`}>
                          {checkbox(`Position ${r.oz} auswählen`)}
                          <td className={`${cell} py-1.5 tabular-nums text-app-text-subtle`}>{r.oz}</td>
                          <td className={`${cell} py-1.5`} style={pad}>
                            {r.long_text ? (
                              <details className="group">
                                <summary className="cursor-pointer list-none text-app-text">
                                  {r.short_text || r.long_text.split("\n")[0]}
                                  {r.is_eventual ? <EventualTag /> : null}
                                  {aiMark}
                                </summary>
                                <p className="mt-1 whitespace-pre-line text-xs text-app-text-subtle">{r.long_text}</p>
                              </details>
                            ) : (
                              <span className="text-app-text">
                                {r.short_text}
                                {r.is_eventual ? <EventualTag /> : null}
                                {aiMark}
                              </span>
                            )}
                          </td>
                          <td className={`${cell} py-1.5 text-right tabular-nums whitespace-nowrap`}>{formatQty(r.quantity)}</td>
                          <td className={`${cell} py-1.5 whitespace-nowrap text-app-text-muted`}>{r.unit}</td>
                          {hasPrices ? (
                            <td className={`${cell} py-1.5 text-right tabular-nums whitespace-nowrap text-app-text-muted`}>
                              {r.unit_price != null ? formatEuro(r.unit_price) : ""}
                            </td>
                          ) : null}
                          {hasPrices ? (
                            <td
                              className={`${cell} py-1.5 pr-5 text-right tabular-nums whitespace-nowrap ${r.is_eventual ? "text-app-text-subtle" : "text-app-text"}`}
                              title={r.is_eventual ? "Bedarfsposition, nicht in der Summe" : undefined}
                            >
                              {gp != null ? (r.is_eventual ? `(${formatEuro(gp)})` : formatEuro(gp)) : ""}
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )
        ) : running ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <LoadingSpinner size="md" />
            <p className="text-sm text-app-text-muted">
              {state?.status === "running" ? phaseLabel(state) : "PDF wird geladen…"}
            </p>
            <p className="text-xs text-app-text-subtle">
              Große LVs dauern einige Sekunden, gescannte deutlich länger. Du kannst das Fenster schließen und später wiederkommen.
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-app-text-muted">
              Liest Titel, Positionen, Mengen und Einheiten aus einem PDF-Leistungsverzeichnis. Vor dem Übernehmen siehst du
              eine Vorschau. Das PDF wird dabei im Projekt abgelegt.
            </p>
            {gaebHint && onSwitchToGaeb ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                <span>
                  Im Projekt liegt die GAEB-Datei „{gaebHint}“. Sie ist genauer als ein PDF.
                </span>
                <button type="button" onClick={onSwitchToGaeb} className="font-semibold underline underline-offset-2">
                  GAEB importieren
                </button>
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full text-left rounded-md border border-dashed border-sky-200 bg-app-surface-muted/60 px-4 py-5 hover:bg-sky-50/50 hover:border-sky-300 transition-colors"
            >
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-600">
                  <Upload className="w-5 h-5" />
                </div>
                <p className="text-sm text-app-text-muted">
                  PDF hier auswählen oder{" "}
                  <span className="text-sky-700 font-semibold underline underline-offset-2">Datei wählen</span>
                </p>
                <p className="text-xs text-app-text-subtle">PDF bis 2.000 Seiten (max. 50 MB)</p>
              </div>
            </button>
            <OfferImportSourcePicker
              offeringId={offeringId}
              kind="pdf"
              disabled={running}
              busySource={busySource}
              onPick={(source) => void startExtraction(source)}
            />
          </>
        )}

        {state?.status === "error" ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{state.error}</div>
        ) : null}
        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        ) : null}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,application/pdf"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void startExtraction(f);
        }}
      />

      {state?.status === "done" || state?.status === "error" ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-app-border px-5 py-3">
          {state.status === "done" && rows.length > 0 && hasPrices ? (
            <div className="mr-auto min-w-0 leading-tight">
              <p className="text-sm text-app-text-muted">
                Netto <span className="font-semibold tabular-nums text-app-text">{formatEuro(totals.net)}</span>
                {discountPercent != null ? (
                  <>
                    {" · nach "}
                    {formatPercent(discountPercent)}
                    {" Rabatt "}
                    <span className="tabular-nums text-app-text">{formatEuro(round2(totals.net * (1 - discountPercent / 100)))}</span>
                  </>
                ) : null}
              </p>
              <p className="text-xs text-app-text-subtle">
                {[
                  totalCheck?.ok ? "entspricht der Nettosumme im PDF" : null,
                  totals.eventualNet > 0 ? `ohne Bedarfspositionen (${formatEuro(totals.eventualNet)})` : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || "ausgewählte Positionen"}
              </p>
            </div>
          ) : null}
          <button type="button" onClick={reset} disabled={saving} className={APP_BTN_OUTLINE_CLASS}>
            Anderes PDF
          </button>
          {state.status === "done" && rows.length > 0 ? (
            <button
              type="button"
              disabled={saving || selected.size === 0}
              onClick={() => void runImport()}
              className={APP_BTN_PRIMARY_CLASS}
            >
              {saving ? <LoadingSpinner size="sm" /> : null}
              {saving ? "Übernehme…" : `${selectedItems} Positionen übernehmen`}
            </button>
          ) : null}
        </div>
      ) : null}
    </AppModal>
  );
};
